/* ═══════════════════════════════════════════════════════════════════
   [D-420] Наші APP — реєстр продуктів, які показуються юзерам Media:
   у Хабі («Наші APP») і смужкою на екрані очікування обробки.

   Два різні перемикачі, і їх легко переплутати:

   🔴 «Запущено» — чи існує продукт узагалі. Вимкнений не показується
      нікому й ніколи, навіть тому, кому відкрито персонально.
   🔴 «Видно всім» — загальне право. Його перебиває персональне: у
      картці юзера можна відкрити продукт одній людині, лишивши
      закритим для решти.

   Порожня адреса — не помилка, а стан «ще не запустили»: такий продукт
   ніде не показується, щоб кнопка не вела в нікуди.
   ═══════════════════════════════════════════════════════════════════ */
import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Switch, Chip, Snackbar, Alert, Button,
} from '@mui/material';
import api from '../api';
import AppClicksAll from '../components/AppClicksAll';   // [D-767] хто переходив у наші застосунки

const C = {
  card: '#16202c', line: 'rgba(0,229,160,.22)', txt: '#eaf6ff',
  dim: '#a8bece', neon: '#00e5a0', neonTxt: '#7ffcd3', coral: '#ff7a59',
};

/* [D-509] Презентація продукту для «👁 прев'ю»: тексти (списки — рядок на пункт) і скріни (до 8; стрілки — порядок). */
function PresEditor({ a, onSaved, setToast, setErr }) {
  const pv = a.preview || {};
  // [D-510] OWNER: «Переклади обов'язково». Пишете українською — інші 7 мов перекладаються самі при збереженні;
  // будь-яку мову можна відкрити вкладкою й виправити вручну (наступне збереження української перекладе її знову).
  const [lng, setLng] = useState('uk');
  const src = lng === 'uk' ? (pv.pres || {}) : ((pv.i18n || {})[lng] || {});
  const toF = (p0) => ({ tagline: p0.tagline || '', what: p0.what || '', how: (p0.how || []).join('\n'),
                         benefits: (p0.benefits || []).join('\n'), why: p0.why || '' });
  const [f, setF] = useState(toF(src));
  useEffect(() => { setF(toF(src)); }, [lng, a.id]);   // eslint без react-hooks — як у сусідніх сторінках
  const [busy, setBusy] = useState(false);
  const save = () => {
    setBusy(true);
    api.post(`/our-apps/${a.id}/preview`, { lang: lng, pres: { ...f, how: f.how.split('\n'), benefits: f.benefits.split('\n') } }, { timeout: 120000 })
      .then((r) => { const fl = (r.data && r.data.failed) || [];
        if (fl.length) setErr('Збережено, але переклад зараз недоступний для: ' + fl.join(', ') + ' — натисніть «🌐 Перекласти ще раз» пізніше');
        else setToast(lng === 'uk' ? 'Збережено й перекладено на 7 мов' : 'Переклад збережено');
        onSaved(); })
      .catch(() => setErr('Не вдалося зберегти'))
      .finally(() => setBusy(false));
  };
  const LNG = [['uk', '🇺🇦'], ['en', '🇬🇧'], ['pl', '🇵🇱'], ['de', '🇩🇪'], ['fr', '🇫🇷'], ['it', '🇮🇹'], ['es', '🇪🇸'], ['ru', '(ru)']];
  const upload = async (files) => {
    setBusy(true);
    for (const file of Array.from(files || [])) {
      const fd = new FormData(); fd.append('file', file);
      try { await api.post(`/our-apps/${a.id}/preview/shot`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); }
      catch (e) { setErr((e.response && e.response.data && e.response.data.detail) || 'Скрін не завантажено'); break; }
    }
    setBusy(false); onSaved();
  };
  const shots = pv.shots || [];
  const move = (i, dlt) => {
    const n = shots.map(x => x.name); const j = i + dlt; if (j < 0 || j >= n.length) return;
    [n[i], n[j]] = [n[j], n[i]];
    api.post(`/our-apps/${a.id}/preview/shots-order`, { names: n }).then(onSaved).catch(() => setErr('Не вдалося змінити порядок'));
  };
  const del = (nm) => api.delete(`/our-apps/${a.id}/preview/shot/${encodeURIComponent(nm)}`).then(onSaved).catch(() => setErr('Не вдалося прибрати'));
  const fld = (k, label, rows, ph) => (
    <TextField size="small" label={label} placeholder={ph} value={f[k]} multiline={!!rows} minRows={rows || 1}
      onChange={e => { const v = e.target.value; setF(x => ({ ...x, [k]: v })); }}
      InputLabelProps={{ style: { color: C.dim } }} inputProps={{ style: { color: C.txt, fontSize: 13 } }}
      sx={{ width: '100%', mb: 1.2, '& .MuiOutlinedInput-notchedOutline': { borderColor: C.line } }} />
  );
  return (
    <Box sx={{ mt: 1.4, p: 1.5, borderRadius: 2, border: `1px dashed ${C.line}` }}>
      <Box sx={{ display: 'flex', gap: .6, flexWrap: 'wrap', mb: 1.2, alignItems: 'center' }}>
        {LNG.map(([c, fl]) => (
          <Button key={c} size="small" onClick={() => setLng(c)} variant={lng === c ? 'contained' : 'outlined'}
            sx={{ minWidth: 0, px: 1.1, fontSize: c === 'ru' ? 11 : 15, textTransform: 'none', borderColor: C.line,
                  bgcolor: lng === c ? '#f5c84c' : 'transparent', color: lng === c ? '#1a1300' : C.txt,
                  opacity: c !== 'uk' && !((pv.i18n || {})[c]) ? .45 : 1 }}>{fl}</Button>
        ))}
        <Typography sx={{ fontSize: 11, color: C.dim }}>
          {lng === 'uk' ? 'українська — основна; «Зберегти» перекладе на всі мови' : 'ручна правка цієї мови'}</Typography>
        <Button size="small" disabled={busy} onClick={() => { setBusy(true); api.post(`/our-apps/${a.id}/preview/translate`, {}, { timeout: 120000 })
            .then((r) => { const fl = (r.data && r.data.failed) || []; if (fl.length) setErr('Переклад зараз недоступний для: ' + fl.join(', ')); else setToast('Перекладено на 7 мов'); onSaved(); })
            .catch(() => setErr('Не вдалося перекласти')).finally(() => setBusy(false)); }}
          sx={{ textTransform: 'none', fontSize: 11.5, color: C.neon, border: `1px solid ${C.line}`, px: 1 }}>🌐 Перекласти ще раз</Button>
      </Box>
      {pv.draft && <Typography sx={{ fontSize: 11.5, color: C.coral, mb: 1 }}>✎ Чернетку написав Claude з того, що відомо про продукт — перевірте й виправте; після «Зберегти» позначка зникне.</Typography>}
      {fld('tagline', 'Коротко (під назвою)', 0, 'Одне речення: що це і для кого')}
      {fld('what', '🎯 Для чого', 3)}
      {fld('how', '🕹 Як керувати — кожен крок з нового рядка', 3)}
      {fld('benefits', '💎 Користь — кожен пункт з нового рядка', 3)}
      {fld('why', '❤️ Чому це потрібно кожному', 2)}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <Typography sx={{ fontSize: 12.5, color: C.txt, fontWeight: 700 }}>🖼 Скріни ({shots.length}/8)</Typography>
        {shots.length < 8 && (
          <Button size="small" component="label" disabled={busy} sx={{ textTransform: 'none', fontSize: 12, color: C.neon, border: `1px solid ${C.line}`, px: 1.2 }}>
            {busy ? 'Вантажу…' : '⬆ Додати скріни'}
            <input hidden type="file" multiple accept="image/png,image/jpeg,image/webp" onChange={e => { upload(e.target.files); e.target.value = ''; }} />
          </Button>
        )}
        <Typography sx={{ fontSize: 11, color: C.dim }}>вертикальні знімки екрана телефона виглядають найкраще · до 4 МБ</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 1, mb: 1.4 }}>
        {shots.map((sh, i) => (
          <Box key={sh.name} sx={{ position: 'relative', borderRadius: 1.5, overflow: 'hidden', border: `1px solid ${C.line}`, aspectRatio: '9 / 19.5', bgcolor: '#000' }}>
            <img src={sh.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'space-between', bgcolor: 'rgba(0,0,0,.6)' }}>
              <Button size="small" onClick={() => move(i, -1)} disabled={i === 0} sx={{ minWidth: 0, color: C.txt }}>◀</Button>
              <Button size="small" onClick={() => del(sh.name)} sx={{ minWidth: 0, color: C.coral }}>🗑</Button>
              <Button size="small" onClick={() => move(i, 1)} disabled={i === shots.length - 1} sx={{ minWidth: 0, color: C.txt }}>▶</Button>
            </Box>
          </Box>
        ))}
      </Box>
      <Button size="small" variant="contained" onClick={save} disabled={busy}
        sx={{ bgcolor: C.neon, color: '#04121a', textTransform: 'none', fontWeight: 700 }}>💾 Зберегти презентацію</Button>
    </Box>
  );
}

export default function OurAppsPage() {
  const [rows, setRows] = useState([]);
  const [set, setSet] = useState({});
  const [toast, setToast] = useState('');
  const [err, setErr] = useState('');

  const load = () => {
    api.get('/our-apps').then(r => setRows((r.data && r.data.rows) || []))
      .catch(() => setErr('Не вдалося завантажити список'));
    api.get('/referral/settings').then(r => setSet(r.data || {})).catch(() => {});
  };
  useEffect(load, []);

  // [D-421] Завантаження логотипа файлом.
  //
  // 🔴 Після успіху перечитуємо список, а не правимо стан на місці:
  // сервер додає до URL мітку часу, і саме вона змушує браузер показати
  // НОВИЙ логотип, а не той самий зі свого кеша.
  const [busyLogo, setBusyLogo] = useState(0);
  const uploadLogo = (id, f) => {
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { setErr('Файл більший за 3 МБ'); return; }
    const fd = new FormData();
    fd.append('file', f);
    setBusyLogo(id);
    api.post(`/our-apps/${id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(() => { setToast('Логотип оновлено'); load(); })
      .catch(e => setErr((e.response && e.response.data && e.response.data.detail) || 'Не вдалося завантажити'))
      .finally(() => setBusyLogo(0));
  };
  const dropLogo = (id) => {
    setBusyLogo(id);
    api.delete(`/our-apps/${id}/logo`)
      .then(() => { setToast('Логотип прибрано'); load(); })
      .catch(() => setErr('Не вдалося прибрати'))
      .finally(() => setBusyLogo(0));
  };

  // [D-436] Перетягування картки за ручку ⠿.
  //
  // 🔴 Новий порядок ЗБЕРІГАЄТЬСЯ НА СЕРВЕРІ, не в браузері: продукти
  // бачать усі юзери, і бачити мають однаково. Локальне збереження
  // означало б, що на іншому пристрої порядок інший.
  //
  // На екрані міняємо одразу, не чекаючи відповіді — інакше картка
  // «підстрибувала» б назад на час запиту.
  const [dragId, setDragId] = useState(null);
  const onDrop = (e, targetId) => {
    // 🔴 Id беремо з dataTransfer, а не зі стану: `dragend` подекуди
    // спрацьовує РАНІШЕ за `drop` і встигає обнулити стан — саме тому
    // картка поверталась на місце, наче нічого й не сталось.
    let srcId = dragId;
    try {
      const raw = e && e.dataTransfer && e.dataTransfer.getData('text/plain');
      if (raw) srcId = Number(raw);
    } catch (err) {}
    if (e && e.preventDefault) e.preventDefault();
    if (srcId == null || Number.isNaN(srcId) || srcId === targetId) { setDragId(null); return; }
    const dragIdSafe = srcId;
    const cur = [...rows];
    const from = cur.findIndex(x => x.id === dragIdSafe);
    const to = cur.findIndex(x => x.id === targetId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    const [moved] = cur.splice(from, 1);
    cur.splice(to, 0, moved);
    setRows(cur);
    setDragId(null);
    api.post('/our-apps/reorder', { ids: cur.map(x => x.id) })
      .then(() => setToast('Порядок збережено'))
      .catch(() => { setErr('Не вдалося зберегти порядок'); load(); });
  };

  const patch = (id, data) => {
    api.post(`/our-apps/${id}`, data)
      .then(() => { setToast('Збережено'); load(); })
      .catch(e => setErr((e.response && e.response.data && e.response.data.detail) || 'Помилка'));
  };

  // показ «усім» живе там само, де решта прав — у referral_settings
  const visKey = (a) => 'uniq_hyper_vis_' + a.feature_key;
  // [D-507] без запису в налаштуваннях — як є насправді (сервер рахує з довідника), а не «вимкнено»
  const visAll = (a) => (set[visKey(a)] != null ? String(set[visKey(a)]) === '1' : !!a.vis_all);
  // [D-507] OWNER 09.10: «кнопки на кожен продукт (показати/сховати) для всіх і для учнів партнерів… у партнера… цього меню у нього не
  // повинно бути — це регулюю тільки я». Один перемикач — для учнів УСІХ партнерів (і для нових), діє на Хаб, заставку Студії й кнопки бота.
  const setVisPartners = (a, on) => {
    setRows(rs => rs.map(x => (x.id === a.id ? { ...x, pvis: on ? 'on' : 'off' } : x)));
    api.post(`/our-apps/${a.id}/partners-vis`, { on })
      .then(r => setToast((on ? 'Показано' : 'Сховано') + ' учням партнерів (' + ((r.data && r.data.partners) || 0) + ')'))
      .catch(e => { setErr((e.response && e.response.data && e.response.data.detail) || 'Помилка збереження'); load(); });
  };
  const setVisAll = (a, on) => {
    const patchBody = { [visKey(a)]: on ? '1' : '0' };
    setSet(p => ({ ...p, ...patchBody }));
    api.post('/referral/settings', patchBody)
      .then(() => setToast(on ? 'Відкрито всім' : 'Закрито всім'))
      .catch(() => setErr('Помилка збереження'));
  };

  // [D-509] «👁 прев'ю»: неактивна картка → презентація → «Хочу спробувати» (запит у гілку «📲 Запити на додатки»)
  const [presOpen, setPresOpen] = useState(0);
  const setPreview = (a, on, field = 'on') => {
    setRows(rs => rs.map(x => (x.id === a.id ? { ...x, preview: { ...(x.preview || {}), [field]: on } } : x)));
    api.post(`/our-apps/${a.id}/preview`, { [field]: on })
      .then(() => setToast((field === 'on_partners' ? "Прев'ю учням партнерів " : "Прев'ю ") + (on ? 'увімкнено' : 'вимкнено')))
      .catch(() => { setErr('Помилка збереження'); load(); });
  };

  const fld = (a, k, label, ph) => (
    <TextField size="small" label={label} placeholder={ph}
      defaultValue={a[k] || ''}
      onBlur={e => { if (e.target.value !== (a[k] || '')) patch(a.id, { [k]: e.target.value }); }}
      InputLabelProps={{ style: { color: C.dim } }}
      inputProps={{ style: { color: C.txt, fontSize: 13 } }}
      sx={{ flex: 1, minWidth: 220,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: C.line } }} />
  );

  return (
    <Box sx={{ p: 2, maxWidth: 1000 }}>
      <Typography variant="h5" sx={{ color: C.txt, mb: .5, fontWeight: 700 }}>
        📱 Наші APP
      </Typography>

      {/* [D-767] Переходи — найпершим: саме сюди веде повідомлення бота, яке
          називає застосунок. Раніше їх було видно лише в картці партнера, а
          людину без партнера — ніде. */}
      <AppClicksAll />
      <Typography sx={{ fontSize: 12.5, color: C.dim, mb: 2 }}>
        Ці продукти юзер бачить у Хабі, на екрані очікування обробки (v-3 і Студія v-5) і кнопками в боті під час обробки.
        <br />«👁 прев'ю» — усім, кому продукт не відкрито; «👁 прев'ю учням партнерів» — окремо для учнів партнерів (партнер цього не змінює).
        <br />«Видно всім» — звичайним юзерам; «учням партнерів» — учням усіх партнерів разом (партнер сам цього не змінює).
        <br />«👁 прев'ю» — кому продукт не відкрито, той бачить його сірою карткою: натиск — презентація зі скрінами й кнопкою «Хочу спробувати»;
        запит приходить у вашу групу, гілка «📲 Запити на додатки» — там «✅ Надати доступ», «❌ Відхилити», «💬 Написати».
        Персонально кожному — у картці користувача, розділ «Наші APP».
        <br />Порядок міняється перетягуванням за ручку ⠿ — і зберігається для всіх.
      </Typography>

      {rows.map(a => {
        const on = visAll(a);
        const noHref = !a.href;
        return (
          <Paper key={a.id}
                 onDragOver={e => {
                   // без preventDefault браузер узагалі не дозволить «кинути»
                   e.preventDefault();
                   try { e.dataTransfer.dropEffect = 'move'; } catch (err) {}
                 }}
                 onDrop={e => onDrop(e, a.id)}
                 sx={{ bgcolor: C.card, p: 2, mb: 1.6, backgroundImage: 'none',
                       border: `1px solid ${dragId === a.id ? C.neon : (a.color || C.line)}`,
                       opacity: dragId === a.id ? .55 : 1,
                       transition: 'opacity .15s' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
              {/* ручка: тягнемо саме за неї, щоб перетягування не заважало
                  клацати по полях і перемикачах усередині картки */}
              <Box draggable
                   onDragStart={e => {
                     setDragId(a.id);
                     // 🔴 Без setData Firefox і Safari не починають
                     // перетягування взагалі, а Chrome не завершує його
                     // подією drop — картка просто вертається назад.
                     try {
                       e.dataTransfer.setData('text/plain', String(a.id));
                       e.dataTransfer.effectAllowed = 'move';
                     } catch (err) {}
                   }}
                   onDragEnd={() => setDragId(null)}
                   title="Перетягніть, щоб змінити порядок"
                   sx={{ cursor: 'grab', color: C.dim, fontSize: 18, px: .3,
                         userSelect: 'none', '&:active': { cursor: 'grabbing' },
                         '&:hover': { color: C.neon } }}>
                ⠿
              </Box>
              <Box sx={{ width: 40, height: 40, borderRadius: 1.4, flexShrink: 0,
                         display: 'flex', alignItems: 'center', justifyContent: 'center',
                         bgcolor: 'rgba(0,0,0,.3)', overflow: 'hidden' }}>
                {a.logo_url
                  ? <img src={a.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  : <span style={{ fontSize: 22 }}>{a.icon_emoji}</span>}
              </Box>
              <Box sx={{ minWidth: 140 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700,
                                  color: a.enabled ? C.txt : C.dim }}>
                  {a.title}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: C.dim }}>{a.app_key}</Typography>
              </Box>

              <Box sx={{ flex: 1 }} />

              {noHref && (
                <Chip size="small" label="адресу не задано"
                  sx={{ bgcolor: 'transparent', color: C.coral,
                        border: `1px solid ${C.coral}`, fontSize: 11 }} />
              )}
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: 10, color: C.dim }}>запущено</Typography>
                <Switch size="small" checked={!!a.enabled}
                  onChange={e => patch(a.id, { enabled: e.target.checked })}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: C.neon },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          bgcolor: C.neon, opacity: .45 } }} />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: 10, color: C.dim }}>видно всім</Typography>
                <Switch size="small" checked={on} disabled={noHref || !a.enabled || a.has_switch === false}
                  onChange={e => setVisAll(a, e.target.checked)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: C.neon },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          bgcolor: C.neon, opacity: .45 } }} />
              </Box>
              {/* [D-509] прев'ю: тим, кому продукт НЕ відкрито, — неактивна картка з презентацією */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: 10, color: C.dim }}>👁 прев'ю</Typography>
                <Switch size="small" checked={!!(a.preview && a.preview.on)}
                  onChange={e => setPreview(a, e.target.checked)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#f5c84c' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#f5c84c', opacity: .45 } }} />
              </Box>
              {/* [D-510] прев'ю окремо для учнів партнерів (OWNER: «наразі залиш їм сіру картку, я сам потім перемкну») */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: 10, color: C.dim }}>👁 прев'ю учням партн.</Typography>
                <Switch size="small" checked={!!(a.preview && a.preview.on_partners !== false)} disabled={!(a.preview && a.preview.on)}
                  onChange={e => setPreview(a, e.target.checked, 'on_partners')}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#f5c84c' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#f5c84c', opacity: .45 } }} />
              </Box>
              {/* [D-507] учням партнерів — окремо від «всім»; партнер цього перемикача не має */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: 10, color: a.pvis === 'mixed' ? C.coral : C.dim }}>
                  учням партнерів{a.pvis === 'mixed' ? ' · по-різному' : ''}</Typography>
                <Switch size="small" checked={a.pvis === 'on'} disabled={noHref || !a.enabled || a.has_switch === false}
                  onChange={e => setVisPartners(a, e.target.checked)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#b39ddb' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          bgcolor: '#b39ddb', opacity: .45 } }} />
              </Box>
            </Box>

            {/* [D-509] презентація для прев'ю й запити юзерів */}
            {a.app_key !== 'media' && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 1.2 }}>
                <Button size="small" onClick={() => setPresOpen(o => (o === a.id ? 0 : a.id))}
                  sx={{ textTransform: 'none', fontSize: 12, color: '#f5c84c', border: '1px solid rgba(245,200,76,.4)', px: 1.2 }}>
                  ✎ Презентація {presOpen === a.id ? '▴' : '▾'}</Button>
                {a.preview && a.preview.draft && <Chip size="small" label="✎ чернетка" sx={{ bgcolor: 'transparent', color: C.coral, border: `1px solid ${C.coral}`, fontSize: 11 }} />}
                {a.preview && (a.preview.shots || []).length === 0 && <Chip size="small" label="без скрінів" sx={{ bgcolor: 'transparent', color: C.dim, border: `1px solid ${C.line}`, fontSize: 11 }} />}
                {a.preview && a.preview.pending > 0 && <Chip size="small" label={'📲 ' + a.preview.pending + ' запит(ів) чекає'} sx={{ bgcolor: 'rgba(245,200,76,.14)', color: '#f5c84c', fontSize: 11 }} />}
              </Box>
            )}
            {presOpen === a.id && <PresEditor a={a} onSaved={load} setToast={setToast} setErr={setErr} />}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.4 }}>
              {fld(a, 'href', 'Адреса', 'https://t.me/... або /apps/...')}
              <Box sx={{ display: 'flex', gap: .8, alignItems: 'center' }}>
                <Button size="small" component="label" disabled={busyLogo === a.id}
                  sx={{ textTransform: 'none', fontSize: 12, color: C.neon,
                        border: `1px solid ${C.line}`, px: 1.2 }}>
                  {busyLogo === a.id ? 'Вантажу…' : (a.logo_url ? '🖼 Замінити логотип' : '🖼 Завантажити логотип')}
                  <input hidden type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                    onChange={e => { uploadLogo(a.id, e.target.files && e.target.files[0]); e.target.value = ''; }} />
                </Button>
                {a.logo_url && (
                  <Button size="small" onClick={() => dropLogo(a.id)} disabled={busyLogo === a.id}
                    sx={{ textTransform: 'none', fontSize: 12, color: C.coral, minWidth: 0 }}>
                    прибрати
                  </Button>
                )}
              </Box>
              <TextField size="small" label="Порядок" type="number"
                defaultValue={a.order}
                onBlur={e => patch(a.id, { order: e.target.value })}
                InputLabelProps={{ style: { color: C.dim } }}
                inputProps={{ style: { color: C.txt, fontSize: 13 } }}
                sx={{ width: 96, '& .MuiOutlinedInput-notchedOutline': { borderColor: C.line } }} />
            </Box>

            <Typography sx={{ fontSize: 10.5, color: C.dim, mt: .6 }}>
              Логотип: png / jpg / webp / svg, до 3 МБ. Без нього показується емодзі {a.icon_emoji}
            </Typography>
            <Typography sx={{ fontSize: 11, color: C.dim, mt: 1 }}>
              {!a.enabled
                ? 'Вимкнено повністю — не бачить ніхто, навіть якщо відкрито персонально.'
                : noHref
                  ? 'Поки немає адреси, продукт ніде не показується — щоб кнопка не вела в нікуди.'
                  : on
                    ? 'Відкрито всім. Окремому юзеру можна закрити в його картці.'
                    : 'Закрито всім. Окремому юзеру можна відкрити в його картці.'}
            </Typography>
          </Paper>
        );
      })}

      <Snackbar open={!!toast} autoHideDuration={2200} onClose={() => setToast('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" variant="filled">{toast}</Alert>
      </Snackbar>
      <Snackbar open={!!err} autoHideDuration={4000} onClose={() => setErr('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" variant="filled">{err}</Alert>
      </Snackbar>
    </Box>
  );
}
